import React from 'react';

export function useStorageState<T>(storage: Storage, key: string | undefined, initialValue: T, parse: (value: string) => T = JSON.parse): [T, React.Dispatch<React.SetStateAction<T>>, () => void] {
	const [storedValue, setStoredValue] = React.useState<T>(() => {
		if (typeof key !== 'string' || key.length === 0) {
			return initialValue;
		}
		try {
			// Get from storage by key and parse if found
			const item = storage.getItem(key);
			return item ? parse(item) : initialValue;
		} catch (error) {
			// If an error occurred, log it and return initial value
			console.error(error);
			return initialValue;
		}
	});

	const clearStorageValue = React.useCallback(() => {
		if (key == null) return;
		storage.removeItem(key);
	}, [storage, key]);

	// When storedValue changes, persist it in storage
	React.useEffect(() => {
		if (typeof key === 'string' && key.length > 0) {
			storage.setItem(key, JSON.stringify(storedValue));
		}
	}, [storage, key, storedValue]);

	return [storedValue, setStoredValue, clearStorageValue];
}

export function useStorageRef<T>(
	storage: Storage,
	key: string | undefined,
	initialValue: T,
	parse: (value: string) => T = JSON.parse,
): { ref: React.MutableRefObject<T>; set: (value: T) => void; clear: () => void } {
	const getInitialValue = (): T => {
		if (typeof key !== 'string' || key.length === 0) {
			return initialValue;
		}
		try {
			const item = storage.getItem(key);
			return item ? parse(item) : initialValue;
		} catch (error) {
			console.error(error);
			return initialValue;
		}
	};

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const ref = React.useRef<T>(getInitialValue());

	const set = React.useCallback(
		(value: T) => {
			ref.current = value;
			if (typeof key === 'string' && key.length > 0) {
				storage.setItem(key, JSON.stringify(value));
			}
		},
		[storage, key],
	);

	const clear = React.useCallback(() => {
		ref.current = initialValue;
		if (typeof key === 'string' && key.length > 0) {
			storage.removeItem(key);
		}
	}, [storage, key, initialValue]);

	return { ref, set, clear };
}

